import { z } from 'zod';

/**
 * Shape of the backend error envelope: `{ key, service, detail }`.
 *
 * `key` — the translation-KEY field the resolver whitelists against `errors.json`.
 *   Strict: stage 1 is terminal, so a missing/empty `key` must fall through to the
 *   stage-2 legacy heuristic rather than resolve to a bogus contract match.
 * `service` — observability metadata only (Sentry tagging). Deliberately `z.string()`,
 *   NOT `z.enum(...)`: a strict enum would fail the whole-body parse the moment the
 *   backend adds a new service, degrading every error from that service to the legacy
 *   heuristic. See `API_ERROR_SERVICE` for the known values as typed tagging metadata.
 * `detail` — raw developer-facing payload, Sentry-only by default (see `detailPolicy.ts`
 *   for the allow-listed exceptions rendered to users). Deliberately `z.unknown()`: it
 *   may be a string, object, array, or a literal `null` (R3 — a 5xx body with
 *   `detail: null` must still parse at stage 1, never fall through to stage 2).
 *
 * zod v3 strips unknown keys by default, so extra backend fields never fail the parse.
 * Always use `safeParse` — never `.parse()`, the resolver must not throw.
 */
export const API_ERROR_SERVICE = {
	Auth: 'auth',
	Core: 'core',
	Reports: 'reports',
	Crm: 'crm',
	Alerts: 'alerts',
	Internal: 'internal',
} as const;

export const apiErrorBodySchema = z.object({
	key: z.string().min(1),
	service: z.string().optional(),
	detail: z.unknown().optional(),
});

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;
